/*
 *
 *  LoginDatabaseProvider.tsx
 *
 *
 *  Created by Christopher Fahlin on 11/17/2021
 *
 *  dha-login
 *
 *  Copyright © 2009-2023 United States Government as represented by
 *  the Program Manager of the DHA: Web and Mobile Technology Program Management Office
 *  All Rights Reserved.
 *
 *  Copyright © 2009-2023 Contributors. All Rights Reserved.
 *
 *  THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 *  REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 *  COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 *  AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 *  THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 *  INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 *  REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 *  DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 *  HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 *  RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 *  Government Agency: DHA: Web and Mobile Technology Program Management Office
 *  Government Agency Original Software Designation: dha-login
 *  Government Agency Original Software Title: dha-login
 *  User Registration Requested. Please send email
 *  with your contact information to: robert.a.kayl.civ@mail.mil
 *  Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 *
 */

import type { ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';

import { getKeyFromPassword } from 'dha-encryption';
import type { DHAKeyObject } from 'dha-encryption';

import { LoginDatabase } from '../database/Database';

interface IDatabaseProviderProps {
  customPassword?: string;
  children: ReactNode;
}

interface EncryptedDatabase {
  DHA_Key: DHAKeyObject | undefined;
  database: LoginDatabase;
}

export const LoginDatabaseContext = createContext<EncryptedDatabase>({
  DHA_Key: undefined,
  database: new LoginDatabase(),
});

/**
 * Provider for LoginDatabaseContext
 *
 * @param customPassword (string) - optional password used to generate a key for database encryption.
 * @remarks
 * Should wrap any & all components that need LoginDb access, including AuthProvider
 *
 * @example
 * ```javascript
 * <LoginDatabaseProvider>
 *   <AuthProvider>
 *     <AppRoutes />
 *   </AuthProvider>
 * </LoginDatabaseProvider>
 * ```
 */
export const LoginDatabaseProvider = ({ customPassword, children }: IDatabaseProviderProps) => {
  // Creates initial DHAKey (obj with 'key' & 'iv' properties)
  const [DHA_Key, set_DHA_Key] = useState<DHAKeyObject | undefined>();

  useEffect(() => {
    const makeKey = async () => {
      const pw = customPassword ?? 'SgbFOytC';
      try {
        const eKey = await getKeyFromPassword(pw);
        set_DHA_Key(eKey);
      } catch (err) {
        console.log(err);
      }
    };
    makeKey().catch((e) => console.log(e));
  }, [customPassword]);

  if (DHA_Key !== undefined) {
    return (
      <LoginDatabaseContext.Provider value={{ DHA_Key, database: new LoginDatabase() }}>
        {children}
      </LoginDatabaseContext.Provider>
    );
  } else return <div></div>;
};
